import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import {
  AuthResponse,
  AuthService,
  isTwoFactorChallenge,
} from '../../services/auth.service';
import { LogService } from '../../services/log.service';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, RouterModule],
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit {
  private log = inject(LogService);
  // DÉTAILS: On injecte les services et outils nécessaires via `inject`.
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  loginForm: FormGroup;
  isLoading = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  // Seconde étape (double authentification) : jeton intermédiaire reçu après le mot de passe
  twoFactorChallenge: string | null = null;
  codeForm = this.fb.nonNullable.group({
    code: ['', [Validators.required, Validators.maxLength(20)]],
  });

  constructor() {
    // DÉTAILS: On initialise le formulaire réactif avec ses contrôles et validateurs.
    // C'est la méthode moderne et robuste pour gérer les formulaires dans Angular.
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    // DÉTAILS: A l'initialisation, on vérifie si l'URL contient un paramètre 'verified=true'.
    // Si c'est le cas, cela signifie que l'utilisateur vient de vérifier son e-mail,
    // on affiche donc un message de succès pour le guider.
    this.route.queryParams.subscribe((params) => {
      if (params['verified'] === 'true') {
        this.successMessage =
          'Votre compte a été vérifié avec succès. Vous pouvez maintenant vous connecter.';
      }
      if (params['deleted'] === 'true') {
        this.successMessage =
          'Votre compte et vos données ont été supprimés. Merci d’avoir utilisé Analytica.';
      }
      this.cdr.markForCheck();
    });
  }

  /**
   * DÉTAILS: Méthode appelée lors de la soumission du formulaire.
   * Elle vérifie la validité du formulaire, puis appelle le service d'authentification.
   */
  onSubmit(): void {
    if (this.loginForm.invalid) {
      // Marque tous les champs comme 'touchés' pour afficher les erreurs de validation.
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    const { email, password } = this.loginForm.value;

    this.authService.login(email, password).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (isTwoFactorChallenge(response)) {
          this.twoFactorChallenge = response.challengeToken;
          this.codeForm.reset();
          this.cdr.markForCheck();
          return;
        }
        this.redirectAfterLogin(response);
      },
      error: (err) => {
        // DÉTAILS: En cas d'erreur, on affiche un message à l'utilisateur.
        this.errorMessage =
          err.error?.message ||
          err.message ||
          'Identifiants incorrects. Veuillez réessayer.';
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** Seconde étape : code à 6 chiffres de l'application ou code de secours. */
  onSubmitCode(): void {
    if (!this.twoFactorChallenge || this.codeForm.invalid) {
      this.codeForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    this.authService
      .loginTwoFactor(
        this.twoFactorChallenge,
        this.codeForm.getRawValue().code.trim(),
      )
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.redirectAfterLogin(response);
        },
        error: (err) => {
          this.errorMessage =
            err.status === 429
              ? err.error?.message || 'Trop de tentatives, réessayez plus tard.'
              : 'Code invalide ou délai dépassé. Réessayez, ou recommencez la connexion.';
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  /** Retour à la saisie du mot de passe (par exemple après expiration du délai de 5 minutes). */
  cancelTwoFactor(): void {
    this.twoFactorChallenge = null;
    this.errorMessage = null;
    this.codeForm.reset();
    this.cdr.markForCheck();
  }

  private redirectAfterLogin(response: AuthResponse): void {
    this.log.debug('📥 Réponse login complète:', response);
    this.log.debug('📥 response.user:', response.user);
    this.log.debug(
      '📥 onboardingCompleted:',
      response.user?.onboardingCompleted,
    );

    // DÉTAILS: LOGIQUE DE REDIRECTION CONDITIONNELLE (Flow 1 vs Flow 2)
    // On vérifie si un plan payant était en attente dans le service d'authentification.
    const pendingPlan = this.authService.getPendingPlan();
    this.log.debug('==========> pendingPlan=' + pendingPlan);
    const subscriptionStatus = response.subscription?.status;
    const onboardingCompleted = response.user?.onboardingCompleted;

    this.log.debug('🔀 Décision de redirection:', {
      pendingPlan,
      subscriptionStatus,
      onboardingCompleted,
    });

    if (
      pendingPlan &&
      pendingPlan !== 'free' &&
      subscriptionStatus !== 'active'
    ) {
      // Si oui, on redirige vers la page de paiement avec le bon plan.
      this.log.debug('➡️ Redirection vers /checkout');
      this.router.navigate(['/checkout'], {
        queryParams: { plan: pendingPlan },
      });
    } else if (!onboardingCompleted) {
      // Si l'onboarding n'est pas terminé, rediriger vers l'onboarding
      this.log.debug(
        '➡️ Redirection vers /onboarding/step/1 (onboarding incomplet)',
      );
      this.router.navigate(['/onboarding/step/1']);
    } else {
      // Sinon (flux gratuit ou connexion normale avec onboarding terminé), on redirige vers le dashboard.
      this.log.debug('➡️ Redirection vers /dashboard (onboarding complété)');
      this.router.navigate(['/dashboard']);
    }
    this.cdr.markForCheck();
  }
}
